import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pause, Play, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const CADENCE_LABEL = {
  weekly: "Weekly",
  biweekly: "Every 2 weeks",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annually: "Annually",
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—";

const emptyLine = () => ({ _uid: Math.random().toString(36).slice(2), description: "", quantity: 1, unit_price: 0 });

const RecurringInvoices = () => {
  const [schedules, setSchedules] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    project_id: "",
    cadence: "monthly",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: "",
    tax_rate: 0.20,
    currency: "GBP",
    payment_terms_days: 30,
    notes: "",
    line_items: [emptyLine()],
  });

  const load = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get("/recurring-invoices"),
        api.get("/projects"),
      ]);
      setSchedules(s.data);
      setProjects(p.data);
    } catch { toast.error("Failed to load recurring schedules"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const updateLine = (idx, field, val) => {
    const items = [...form.line_items];
    items[idx] = { ...items[idx], [field]: val };
    setForm({ ...form, line_items: items });
  };

  const addLine = () => setForm({ ...form, line_items: [...form.line_items, emptyLine()] });

  const removeLine = (idx) => {
    const items = form.line_items.filter((_, i) => i !== idx);
    setForm({ ...form, line_items: items.length ? items : [emptyLine()] });
  };

  const subtotal = form.line_items.reduce((s, li) => s + (Number(li.quantity) || 0) * (Number(li.unit_price) || 0), 0);
  const tax = subtotal * (Number(form.tax_rate) || 0);
  const total = subtotal + tax;

  const create = async () => {
    if (!form.project_id) return toast.error("Pick a project");
    if (!form.line_items.some((li) => li.description.trim())) return toast.error("Add at least one line item");
    setBusy(true);
    try {
      await api.post("/recurring-invoices", {
        ...form,
        tax_rate: Number(form.tax_rate),
        payment_terms_days: Number(form.payment_terms_days),
        end_date: form.end_date || null,
        line_items: form.line_items.filter((li) => li.description.trim()).map(({ _uid, ...li }) => ({
          ...li,
          quantity: Number(li.quantity),
          unit_price: Number(li.unit_price),
        })),
      });
      toast.success("Recurring schedule created");
      setOpen(false);
      setForm({ project_id: "", cadence: "monthly", start_date: new Date().toISOString().slice(0, 10), end_date: "", tax_rate: 0.20, currency: "GBP", payment_terms_days: 30, notes: "", line_items: [emptyLine()] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  const togglePause = async (s) => {
    try {
      await api.patch(`/recurring-invoices/${s.schedule_id}`, { paused: !s.paused });
      toast.success(s.paused ? "Resumed" : "Paused");
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (s) => {
    if (!window.confirm("Cancel this recurring schedule? Existing invoices are kept.")) return;
    try {
      await api.delete(`/recurring-invoices/${s.schedule_id}`);
      toast.success("Schedule cancelled");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Billing"
        title="Recurring Invoices"
        description="Set up automatic invoice generation on a repeating cadence."
        actions={
          <Button onClick={() => setOpen(true)} className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="new-recurring-btn">
            <Plus className="w-4 h-4 mr-1" /> New schedule
          </Button>
        }
      />

      <div className="px-5 sm:px-8 lg:px-12 py-8">
        {loading ? (
          <div className="text-sm text-gray-500 py-12 text-center">Loading…</div>
        ) : schedules.length === 0 ? (
          <div className="border border-dashed border-[#374151] py-16 text-center" data-testid="recurring-empty">
            <RefreshCw className="w-8 h-8 text-gray-600 mx-auto mb-3" />
            <div className="text-sm text-gray-500">No recurring schedules yet.</div>
            <div className="text-xs text-gray-600 mt-1">Create one to auto-generate invoices on a weekly, monthly, or quarterly cadence.</div>
          </div>
        ) : (
          <div className="border border-[#1F2937]">
            <div className="grid grid-cols-12 gap-4 px-5 py-2.5 border-b border-[#1F2937] bg-[#111827] text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">
              <div className="col-span-3">Project</div>
              <div className="col-span-2">Cadence</div>
              <div className="col-span-2">Next run</div>
              <div className="col-span-2">End date</div>
              <div className="col-span-1 text-right">Generated</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-1" />
            </div>
            {schedules.map((s) => {
              const proj = projects.find((p) => p.project_id === s.project_id);
              return (
                <div key={s.schedule_id} className="grid grid-cols-12 gap-4 px-5 py-3.5 border-b border-[#1F2937] bg-[#0D1117] text-white items-center text-sm" data-testid={`schedule-row-${s.schedule_id}`}>
                  <div className="col-span-3 font-medium truncate">{proj?.name || s.project_id}</div>
                  <div className="col-span-2 text-[#9CA3AF]">{CADENCE_LABEL[s.cadence] || s.cadence}</div>
                  <div className="col-span-2 font-mono text-xs text-[#00F5D4]">{fmtDate(s.next_run_date)}</div>
                  <div className="col-span-2 text-xs text-[#6B7280]">{s.end_date ? fmtDate(s.end_date) : "No end"}</div>
                  <div className="col-span-1 text-right font-mono text-xs">{s.invoices_generated || 0}</div>
                  <div className="col-span-1">
                    <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 ${s.paused ? "bg-[#1F2937] text-[#6B7280]" : "bg-[#00F5D4]/10 text-[#00F5D4]"}`}>
                      {s.paused ? "Paused" : "Active"}
                    </span>
                  </div>
                  <div className="col-span-1 flex justify-end gap-1">
                    <button
                      onClick={() => togglePause(s)}
                      className="p-1.5 text-[#6B7280] hover:text-[#F59E0B]"
                      title={s.paused ? "Resume" : "Pause"}
                      data-testid={`toggle-pause-${s.schedule_id}`}
                    >
                      {s.paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      onClick={() => remove(s)}
                      className="p-1.5 text-[#6B7280] hover:text-rose-500"
                      title="Cancel schedule"
                      data-testid={`delete-schedule-${s.schedule_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none bg-[#111827] border-[#374151] text-white sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">New recurring schedule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em]">Project *</Label>
                <select
                  value={form.project_id}
                  onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                  className="w-full mt-1 h-10 rounded-none border border-[#374151] bg-[#0D1117] text-white px-3 text-sm"
                  data-testid="recurring-project-select"
                >
                  <option value="">— pick a project —</option>
                  {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em]">Cadence *</Label>
                <Select value={form.cadence} onValueChange={(v) => setForm({ ...form, cadence: v })}>
                  <SelectTrigger className="rounded-none mt-1 bg-[#0D1117] border-[#374151] text-white" data-testid="recurring-cadence-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#374151] text-white">
                    {Object.entries(CADENCE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em]">First run date *</Label>
                <Input type="date" className="rounded-none mt-1 bg-[#0D1117] border-[#374151] text-white" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="recurring-start-date" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em]">End date (optional)</Label>
                <Input type="date" className="rounded-none mt-1 bg-[#0D1117] border-[#374151] text-white" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="recurring-end-date" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em]">Payment terms (days)</Label>
                <Input type="number" min="0" className="rounded-none mt-1 bg-[#0D1117] border-[#374151] text-white" value={form.payment_terms_days} onChange={(e) => setForm({ ...form, payment_terms_days: e.target.value })} data-testid="recurring-terms" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em]">Tax rate</Label>
                <Input type="number" min="0" max="1" step="0.01" className="rounded-none mt-1 bg-[#0D1117] border-[#374151] text-white" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} data-testid="recurring-tax-rate" />
              </div>
            </div>

            {/* Line items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[11px] uppercase tracking-[0.2em]">Line items</Label>
                <button onClick={addLine} className="text-xs text-[#002FA7] hover:underline" data-testid="recurring-add-line">+ Add line</button>
              </div>
              <div className="space-y-2">
                {form.line_items.map((li, idx) => (
                  <div key={li._uid} className="grid grid-cols-12 gap-2 items-center">
                    <Input
                      className="col-span-6 rounded-none bg-[#0D1117] border-[#374151] text-white text-sm"
                      placeholder="Description"
                      value={li.description}
                      onChange={(e) => updateLine(idx, "description", e.target.value)}
                      data-testid={`recurring-line-desc-${idx}`}
                    />
                    <Input
                      className="col-span-2 rounded-none bg-[#0D1117] border-[#374151] text-white text-sm"
                      type="number" min="0" placeholder="Qty"
                      value={li.quantity}
                      onChange={(e) => updateLine(idx, "quantity", e.target.value)}
                    />
                    <Input
                      className="col-span-3 rounded-none bg-[#0D1117] border-[#374151] text-white text-sm"
                      type="number" min="0" placeholder="Unit price"
                      value={li.unit_price}
                      onChange={(e) => updateLine(idx, "unit_price", e.target.value)}
                    />
                    <button onClick={() => removeLine(idx)} className="col-span-1 text-rose-500 hover:text-rose-400 flex justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-right text-xs text-[#6B7280] space-y-0.5 font-mono">
                <div>Subtotal: {form.currency} {subtotal.toFixed(2)}</div>
                <div>Tax ({(Number(form.tax_rate) * 100).toFixed(0)}%): {form.currency} {tax.toFixed(2)}</div>
                <div className="text-white font-bold text-sm">Total: {form.currency} {total.toFixed(2)}</div>
              </div>
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em]">Notes</Label>
              <Textarea className="rounded-none mt-1 bg-[#0D1117] border-[#374151] text-white text-sm min-h-[60px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="recurring-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none border-[#374151] text-[#9CA3AF]">Cancel</Button>
            <Button onClick={create} disabled={busy} className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="recurring-create-btn">
              {busy ? "Creating…" : "Create schedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecurringInvoices;
